import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Activity, Store, ClipboardList, Wallet, RefreshCw, PackageSearch, UtensilsCrossed, Ticket, LayoutDashboard } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { founderOpsOverview, founderBaseSplit, founderStockMovements } from "../lib/api";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const GOLD = "#C9A227";

const Stat = ({ label, value, color = GREEN, testid }) => (
  <div className="rounded-2xl bg-white p-4" style={{ border: "1px solid #eef0ef" }} data-testid={testid}>
    <div className="text-2xl font-extrabold" style={{ color }}>{value}</div>
    <div className="text-xs font-semibold mt-0.5" style={{ color: "#64748b" }}>{label}</div>
  </div>
);

export default function FounderCockpit() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const [ov, setOv] = useState(null);
  const [fin, setFin] = useState(null);
  const [mov, setMov] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setErr(false);
    Promise.all([founderOpsOverview(), founderBaseSplit().catch(() => null), founderStockMovements(null, 100).catch(() => null)])
      .then(([o, f, m]) => { setOv(o); setFin(f); setMov(m); })
      .catch(() => { setErr(true); toast.error("Chargement de la supervision impossible"); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="fc-denied">
        <p style={{ color: "#64748b" }}>Cockpit réservé au Founder (admin).</p>
      </div>
    );
  }

  const P = ov?.partners;
  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="founder-cockpit">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity size={22} color={ORANGE} />
          <h1 className="text-2xl font-extrabold" style={{ color: "#0b1220" }}>Supervision partenaires</h1>
        </div>
        <button data-testid="fc-refresh" onClick={load} disabled={loading}
          className="flex items-center gap-1.5 text-sm font-bold px-3 h-10 rounded-xl text-white" style={{ background: GREEN, opacity: loading ? 0.6 : 1 }}>
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      <Link to="/restaurant-partenaire" data-testid="fc-manage-catalog"
        className="flex items-center justify-between rounded-2xl px-4 py-3.5 mb-3 text-white"
        style={{ background: GREEN }}>
        <span className="flex items-center gap-2 font-bold">
          <UtensilsCrossed size={18} /> Gérer le catalogue La BASE Maison
        </span>
        <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }}>
          Kiosk · Pharmacie · Restaurant · Boissons
        </span>
      </Link>

      <Link to="/founder/formules" data-testid="fc-formules"
        className="flex items-center justify-between rounded-2xl px-4 py-3.5 mb-6 text-white"
        style={{ background: ORANGE }}>
        <span className="flex items-center gap-2 font-bold">
          <Ticket size={18} /> Formules commerciales La BASE
        </span>
        <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }}>
          Livraison offerte · 2e à -50 % · codes
        </span>
      </Link>

      <Link to="/founder/preface" data-testid="fc-preface"
        className="flex items-center justify-between rounded-2xl px-4 py-3.5 mb-6 text-white"
        style={{ background: "#0b1220" }}>
        <span className="flex items-center gap-2 font-bold">
          <LayoutDashboard size={18} /> Contenu de la Préface (bulle + cubes)
        </span>
        <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }}>
          Éditer les textes
        </span>
      </Link>

      {loading ? (
        <div className="h-40 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} data-testid="fc-loading" />
      ) : err ? (
        <div className="text-center py-10"><p style={{ color: "#64748b" }}>Erreur de chargement.</p>
          <button data-testid="fc-retry" onClick={load} className="mt-4 rounded-xl font-bold text-white px-5 py-2" style={{ background: GREEN }}>Réessayer</button></div>
      ) : (
        <div className="space-y-8">
          {/* Partenaires */}
          <section data-testid="fc-partners">
            <div className="flex items-center gap-2 mb-3"><Store size={18} color={GREEN} /><h2 className="font-extrabold" style={{ color: "#0b1220" }}>Partenaires</h2></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat testid="fc-p-total" label="Total" value={P?.total ?? 0} />
              <Stat testid="fc-p-active" label="Actifs" value={P?.active ?? 0} />
              <Stat testid="fc-p-kitchens" label="Cuisines ouvertes" value={P?.kitchens_open ?? 0} color={ORANGE} />
              <Stat testid="fc-p-suspended" label="Suspendus" value={P?.suspended ?? 0} color="#dc2626" />
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(P?.by_activity || {}).map(([a, n]) => (
                <span key={a} className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: "#f1f6f4", color: GREEN }} data-testid={`fc-act-${a}`}>{a} · {n}</span>
              ))}
            </div>
          </section>

          {/* Commandes par statut */}
          <section data-testid="fc-orders">
            <div className="flex items-center gap-2 mb-3"><ClipboardList size={18} color={GREEN} /><h2 className="font-extrabold" style={{ color: "#0b1220" }}>Commandes (cuisine)</h2></div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(ov?.orders_by_status || {}).map(([st, n]) => (
                <div key={st} className="rounded-xl bg-white px-3 py-2" style={{ border: "1px solid #eef0ef" }} data-testid={`fc-ord-${st}`}>
                  <span className="text-lg font-extrabold" style={{ color: n > 0 ? GREEN : "#94a3b8" }}>{n}</span>
                  <span className="text-xs ml-1.5" style={{ color: "#64748b" }}>{st}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Finance par base */}
          <section data-testid="fc-finance">
            <div className="flex items-center gap-2 mb-1"><Wallet size={18} color={GOLD} /><h2 className="font-extrabold" style={{ color: "#0b1220" }}>Finance — répartition par base</h2></div>
            <p className="text-xs mb-3" style={{ color: "#94a3b8" }}>Comptabilité interne. LIVE = réel · SIMULATION = fixtures.</p>
            {(fin?.live?.length || fin?.simulation?.length) ? (
              <div className="rounded-2xl bg-white p-4 overflow-x-auto" style={{ border: "1px solid #eef0ef" }}>
                <table className="w-full text-sm" data-testid="fc-fin-table">
                  <thead><tr style={{ color: "#64748b" }} className="text-left text-xs">
                    <th className="py-1 pr-3">Base</th><th className="py-1 pr-3">Brut</th><th className="py-1 pr-3">Part vendeuse</th>
                    <th className="py-1 pr-3">Commission</th><th className="py-1 pr-3">Livraison</th><th className="py-1">Gains exécutant</th>
                  </tr></thead>
                  <tbody>
                    {(fin?.live || []).map((b, i) => (
                      <tr key={`l${i}`} data-testid={`fc-fin-live-${i}`} className="border-t" style={{ borderColor: "#f1f5f9" }}>
                        <td className="py-1.5 pr-3 font-bold" style={{ color: GREEN }}>{b.base || b.base_id || "—"} <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1" style={{ background: "#eafff6", color: GREEN }}>LIVE</span></td>
                        <td className="py-1.5 pr-3">{b.brut_produits_chf?.toFixed(2)}</td>
                        <td className="py-1.5 pr-3">{b.part_vendeuse_chf?.toFixed(2)}</td>
                        <td className="py-1.5 pr-3">{b.part_commission_chf?.toFixed(2)}</td>
                        <td className="py-1.5 pr-3">{b.frais_livraison_chf?.toFixed(2)}</td>
                        <td className="py-1.5 font-bold" style={{ color: ORANGE }}>{b.gains_executant_chf?.toFixed(2)}</td>
                      </tr>
                    ))}
                    {(fin?.simulation || []).map((b, i) => (
                      <tr key={`s${i}`} data-testid={`fc-fin-sim-${i}`} className="border-t" style={{ borderColor: "#f1f5f9", opacity: 0.7 }}>
                        <td className="py-1.5 pr-3 font-bold" style={{ color: "#64748b" }}>{b.base || b.base_id || "—"} <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1" style={{ background: "#eff6ff", color: "#1d4ed8" }}>SIM</span></td>
                        <td className="py-1.5 pr-3">{b.brut_produits_chf?.toFixed(2)}</td>
                        <td className="py-1.5 pr-3">{b.part_vendeuse_chf?.toFixed(2)}</td>
                        <td className="py-1.5 pr-3">{b.part_commission_chf?.toFixed(2)}</td>
                        <td className="py-1.5 pr-3">{b.frais_livraison_chf?.toFixed(2)}</td>
                        <td className="py-1.5">{b.gains_executant_chf?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm" style={{ color: "#94a3b8" }}>Aucune donnée financière.</p>}
          </section>

          {/* Mouvements de stock */}
          <section data-testid="fc-movements">
            <div className="flex items-center gap-2 mb-3"><PackageSearch size={18} color={GREEN} /><h2 className="font-extrabold" style={{ color: "#0b1220" }}>Mouvements de stock récents</h2></div>
            {(mov?.movements?.length) ? (
              <div className="rounded-2xl bg-white divide-y" style={{ border: "1px solid #eef0ef", borderColor: "#eef0ef" }}>
                {mov.movements.slice(0, 30).map((m, i) => (
                  <div key={i} data-testid={`fc-mov-${i}`} className="flex items-center justify-between px-4 py-2.5 gap-3" style={{ borderColor: "#f1f5f9" }}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: "#1f2937" }}>{m.product_name || "—"}{m.choice_name ? ` · ${m.choice_name}` : ""}</p>
                      <p className="text-xs" style={{ color: "#94a3b8" }}>{m.scope} · {m.reason} · {(m.timestamp || "").slice(0, 16).replace("T", " ")}</p>
                    </div>
                    <span className="text-sm font-extrabold shrink-0" style={{ color: GREEN }}>{m.qty}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm" style={{ color: "#94a3b8" }}>Aucun mouvement enregistré.</p>}
          </section>
        </div>
      )}
    </div>
  );
}
